import React, {useEffect, useState} from 'react';
import {
    DatePicker,
    DateRangePicker,
    InputAdornment,
    Option,
    Select,
    Toast
} from '@deere/fuel-react';

import {Button, Container, Grid, TextField} from '@mui/material';
import {Add, CalendarMonth, Delete, Schedule} from '@mui/icons-material';
import './RequestClass.css';
import InstructorSelect from 'components/InstructorSelect';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';

interface RequestClassProps {
    cdiCourses: any;
    submitForm: (responseData: any, courseDetails: any) => (e: any) => void;
    setRequestClass: any;
    userType: string;
    requestType: string;
    classId: string;
}

const itemType = ['VILT', 'WEBINAR', 'DLC'];

const classStartDay = 7;
const DEFAULT_START_HOUR = 8; // 8 AM
const EDIT_CLASS_SIZE = 12;
const NEW_CLASS_SIZE = 3;
const NEW_COURSE_SIZE = 8;

const handleInstructorChange = (data: any, state: any, setState: any, requestType: string): any => {
    if (requestType === 'Edit') {
        const selectedInstructor = state.responseData['session-1'].instructor;
        const instructorDetails = data?.find((instructor: any) => instructor.instructorId === selectedInstructor) || '';

        setState((prevState: typeof state) => ({
            ...prevState,
            responseData: {
                ...prevState.responseData,
                'session-1': {
                    ...prevState.responseData['session-1'],
                    instructor: JSON.stringify(instructorDetails)
                }
            }
        }));
    }
    setState((prevState: typeof state) => ({
        ...prevState,
        setInstructors: data
    }));
};

const getClassGridSize = (requestType: string): number => {
    return requestType === 'New' ? NEW_CLASS_SIZE : EDIT_CLASS_SIZE;
};

const getCourseGridSize = (requestType: string): number => {
    return requestType === 'New' ? NEW_COURSE_SIZE : EDIT_CLASS_SIZE;
};

const cancelRequestClass = (setRequestClass: any): any => {
    setRequestClass((prev: any) => ({
        ...prev,
        requestClass: false
    }));
};

const checkValidEndDate = (key: string, value: string, existingDict: any): string => {
    if (key === 'endTime' && !value) {
        return existingDict.endTime;
    }
    return value;
};

const checkWeekend = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    const weekendDay = 6;

    return dayOfWeek !== 0 && dayOfWeek !== weekendDay; // Exclude Sundays (0) and Saturdays (6)
};

const calculateDaysBetweenDates = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const MILLISECONDS_IN_A_DAY = 86400000; // 24 * 60 * 60 * 1000
    let daysBetween = 0;

    for (let date = start; date <= end; date = new Date(date.getTime() + MILLISECONDS_IN_A_DAY)) {
        if (checkWeekend(date)) {
            daysBetween += 1;
        }
    }
    return daysBetween;
};

const checkRequiredFields = (numberOfDays: number, startTime: string): boolean => {
    if (numberOfDays && startTime) {
        return false;
    }
    return true;
};

const calculateEndTime = (key: string, startTime: string, courseHours: string, numberOfDays: number): any => {
    const listOfKeys = ['dates', 'startTime', 'endTime'];

    if (checkRequiredFields(numberOfDays, startTime) || !listOfKeys.includes(key)) {
        return null;
    }

    const MILLISECONDS_IN_AN_HOUR = 3600000; // 60 * 60 * 1000
    const endTime = new Date(new Date(startTime).getTime() + parseInt(courseHours, 10) * MILLISECONDS_IN_AN_HOUR / numberOfDays);

    return {
        endTime,
        defaultEndHours: endTime.getHours(),
        defaultEndMinutes: endTime.getMinutes()
    };
};

const handleCouseHours = (courseHours: string, key: string, event: any, existingDict: any): any => {
    if (key === 'dates') {
        const numberOfDays = calculateDaysBetweenDates(event.value.startDate, event.value.endDate);
        const endTime = calculateEndTime(key, existingDict.startTime, courseHours, numberOfDays);

        return {
            numberOfDays,
            ...endTime
        };
    }
    if (key === 'startTime') {
        return {
            ...calculateEndTime(key, event.value, courseHours, existingDict.numberOfDays)
        };
    }
    return null;
};

const getCssClassName = (selectedCDICourseType: any): any => {
    return itemType.includes(selectedCDICourseType) ? 'required-field' : 'optional-field';
};

interface StateAndSetState {
    state: any;
    setState: React.Dispatch<React.SetStateAction<any>>;
}

const handleSessionDataChange = (event: any, session: string, key: string, {
    state, setState
}: StateAndSetState): void => {
    let value = event;

    setState((prevState: any) => {
        const existingDict = prevState.responseData[session] || {};

        let updatedDict;

        if (key === 'secondaryInstructor') {
            updatedDict = {
                ...existingDict,
                [key]: {
                    ...existingDict[key],
                    ...value
                }
            };
        } else {
            const extraValue = handleCouseHours(state.selectedCDICourseHours, key, event, existingDict);

            value = checkValidEndDate(key, event.value, existingDict);
            updatedDict = {
                ...existingDict,
                [key]: value,
                ...extraValue
            };
        }

        return {
            ...prevState,
            responseData: {
                ...prevState.responseData,
                [session]: updatedDict
            }
        };
    });
};

const showVirtualClassLinkField = (state: any, setState: React.Dispatch<React.SetStateAction<any>>, i: any, t: (key: string) => string): any => {
    if (itemType.includes(state.selectedCDICourseType)) {
        return (
            <>
                <TextField
                    id={`session-${i + 1}-virtualClassLink`}
                    inputProps={{
                        maxLength: 500
                    }}
                    label={
                        <span className={getCssClassName(state.selectedCDICourseType)}>
                            {t('requestClass.virtualClassLink')}
                        </span>
                    }
                    onChange={(e): void => handleSessionDataChange(e.target, `session-${i + 1}`, 'virtualClassLink', {
                        state,
                        setState
                    })}
                    value={state.responseData[`session-${i + 1}`].virtualClassLink}
                />
                <div className='additional-margin'/>
            </>
        );
    }
    return null;
};

const getMaxQtyValue = (sessionKey: string, state: any): string => {
    const sessionMaxQty = state.responseData[sessionKey]?.maxQty;

    if (sessionMaxQty) {
        return sessionMaxQty;
    }
    return state.maxQty.length > 0 ? state.maxQty[state.maxQty.length - 1] : '';
};

const checkLoader = (state: any, t: (key: string) => string): string => {
    if (state.selectedCDICourseTitle && state.setInstructors[0]?.instructorId === 'Loading...') {
        return t('requestClass.loadingInstructors');
    }
    return '';
};

const RequestClass: React.FC<RequestClassProps> = ({
    cdiCourses,
    submitForm,
    setRequestClass,
    userType,
    requestType,
    classId
}) => {
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const [state, setState] = useState({
        selectedCDICourseTitle: '',
        selectedCDICourseId: '',
        selectedCDICourseType: '',
        selectedCDICourseHours: '',
        selectedCDICourseMaxQty: '',
        selectedRevisionDate: '',
        setInstructors: [{
            instructorId: 'Loading...',
            instructorName: 'Loading...',
            domainId: '',
            dealership: ''
        }],
        timeZones: [
            {
                'timezoneId': '',
                'description': ''
            }
        ],
        responseData: {
            'session-1':
                {
                    'instructor': '',
                    'secondaryInstructor': {
                        'secondary-instructor-1': ''
                    },
                    'dates': {
                        'startDate': '',
                        'endDate': ''
                    },
                    'startTime': '',
                    'endTime': '',
                    'timeZone': [
                        {
                            'timezoneId': '',
                            'description': ''
                        }
                    ],
                    'dealerFacility': '',
                    'virtualClassLink': '',
                    'maxQty': '0', // Default to maximum initial value
                    'additionalComments': '',
                    'numberOfDays': 0,
                    'defaultEndHours': 0,
                    'defaultEndMinutes': 0
                }
        } as any,
        maxQty: Array.from({
            length: 24
        }, (_, i) => (i + 1).toString()),
        totalSessions: 1,
        maxSecondaryInstructors: 4
    });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/timezones`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/json'
            }
        }).then((res) => {
            return res.json();
        }).then((data) => {
            setState((prevState: any) => ({
                ...prevState,
                timeZones: data
            }));
            return data;
        }).catch((error) => console.log(error));
        // let classId = '5914332';

        if (requestType === 'Edit' && classId) {
            // specific actions for the Edit request type
            fetch(`${process.env.REACT_APP_BACKEND_URL}/classrequest/details/${classId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                    'Content-Type': 'application/json; charset=UTF-8',
                    'Accept': 'application/json'
                }
            }).then((res) => res.json()).then((data) => {
                const result: any = {};

                data.secondaryInstructors.forEach((instructorId: string, index: number) => {
                    if (instructorId) { // Only add if instructorId is not empty
                        result[`secondary-instructor-${index + 1}`] = instructorId;
                    }
                });
                setState((prevState) => ({
                    ...prevState,
                    selectedCDICourseTitle: cdiCourses[0].courseTitle,
                    selectedCDICourseId: cdiCourses[0].courseId,
                    selectedCDICourseType: cdiCourses[0].courseType,
                    selectedCDICourseHours: cdiCourses[0].courseHours,
                    selectedCDICourseMaxQty: cdiCourses[0].courseMaxQty,
                    selectedRevisionDate: cdiCourses[0].revisionDate,
                    responseData: {
                        'session-1':
                            {
                                'instructor': data.instructorId,
                                'secondaryInstructor': result,
                                'dates': {
                                    'startDate': new Date(data.startDate),
                                    'endDate': new Date(data.endDate)
                                },
                                'startTime': new Date(data.startTime),
                                'endTime': new Date(data.endTime),
                                'timeZone': data.timeZone,
                                'dealerFacility': data.facilityAddress,
                                'virtualClassLink': data.virtualLink,
                                'maxQty': data.maxQty,
                                'additionalComments': data.additionalComments,
                                'numberOfDays': 0,
                                'defaultEndHours': 0,
                                'defaultEndMinutes': 0
                            }
                    },
                    totalSessions: 1,
                    maxQty: Array.from({
                        length: parseInt(cdiCourses[0].courseMaxQty, 10)
                    }, (_, i) => (i + 1).toString())
                })
                );
                return data;
            }).catch((error) => console.log(error));
        }
    }, [authState?.accessToken?.accessToken, cdiCourses, classId, requestType]);

    const handleCourseChange = (event: any): void => {
        const value = event.currentTarget.value;
        const [courseId, courseType, courseHours, courseMaxQty, revisionDate, ...courseTitleParts] = value.split(' ');
        const courseTitle = courseTitleParts.join(' ');

        setState((prevState) => ({
            ...prevState,
            selectedCDICourseTitle: courseTitle,
            selectedCDICourseId: courseId,
            selectedCDICourseType: courseType,
            selectedCDICourseHours: courseHours,
            selectedCDICourseMaxQty: courseMaxQty,
            selectedRevisionDate: revisionDate,
            responseData: {
                'session-1':
                    {
                        'instructor': '',
                        'secondaryInstructor': {
                            'secondary-instructor-1': ''
                        },
                        'dates': {
                            'startDate': '',
                            'endDate': ''
                        },
                        'startTime': new Date(new Date().setHours(DEFAULT_START_HOUR, 0, 0, 0)),
                        'endTime': '',
                        'timeZone': [
                            {
                                'timezoneId': '',
                                'description': ''
                            }
                        ],
                        'dealerFacility': itemType.includes(courseType) ? 'VIRTUAL2' : '',
                        'virtualClassLink': '',
                        'maxQty': courseMaxQty, // Set to the course's maximum quantity
                        'additionalComments': '',
                        'numberOfDays': 0,
                        'defaultEndHours': 0,
                        'defaultEndMinutes': 0
                    }
            },
            totalSessions: 1,
            maxQty: Array.from({
                length: parseInt(courseMaxQty, 10)
            }, (_, i) => (i + 1).toString())
        }));
    };

    const addRemoveInstructor = (session: string, type: string): void => {
        if (type === 'add') {
            setState((prevState) => {
                const existingSessionData = {
                    ...prevState.responseData[session].secondaryInstructor
                };
                const existingArray = Object.keys(existingSessionData || {}).length;

                return {
                    ...prevState,
                    responseData: {
                        ...prevState.responseData,
                        [session]: {
                            ...prevState.responseData[session],
                            secondaryInstructor: {
                                ...prevState.responseData[session].secondaryInstructor,
                                [`secondary-instructor-${existingArray + 1}`]: ''
                            }
                        }
                    }
                };
            });
        } else {
            setState((prevState) => {
                const existingSessionData = {
                    ...prevState.responseData[session].secondaryInstructor
                };
                const existingArray = Object.keys(existingSessionData || {}).length;

                delete existingSessionData[`secondary-instructor-${existingArray}`];

                return {
                    ...prevState,
                    responseData: {
                        ...prevState.responseData,
                        [session]: {
                            ...prevState.responseData[session],
                            secondaryInstructor: {
                                ...existingSessionData
                            }
                        }
                    }
                };
            });
        }
    };

    const handleSessionAdd = (session: string): any => {
        const sessionLimit = 12;

        if (state.totalSessions >= sessionLimit) {
            return (<Toast
                title={t('requestClass.maxSessionsTitle')}
                variant='info'
            >
                {t('requestClass.maxSessionsMessage')}
            </Toast>);
        }
        setState((prevState) => {
            const newSessionKey = `session-${prevState.totalSessions + 1}`;
            const existingSessionData = prevState.responseData[session] || {};
            const newSessionData = {
                ...existingSessionData,
                dates: {
                    startDate: '',
                    endDate: ''
                },
                startTime: '',
                endTime: ''
            };

            return {
                ...prevState,
                totalSessions: prevState.totalSessions + 1,
                responseData: {
                    ...prevState.responseData,
                    [newSessionKey]: newSessionData
                }
            };
        });
        return null;
    };

    const handleSessionDelete = (session: string): any => {
        const respData = state.responseData;
        const keys = Object.keys(state.responseData);
        const lastKey = keys[keys.length - 1];

        respData[session] = respData[lastKey];
        setState((prevState) => ({
            ...prevState,
            totalSessions: prevState.totalSessions - 1,
            responseData: (({
                [lastKey]: _, ...rest
            }): any => rest)(respData)
        }));
        return null;
    };

    const dateConverter = (date: any): any => {
        if (!date) {
            return '';
        }
        return new Date(date);
    };

    return (
        <Container
            className='container'
            maxWidth={'lg'}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <h2 className='header'>{requestType === 'New' ? t('requestClass.newClass') : t('requestClass.editClass')}</h2>
                {requestType === 'New' ? <Button
                    onClick={(): void => cancelRequestClass(setRequestClass)}
                    variant={'primary'}
                >
                    {t('requestClass.cancelRequest')}
                </Button> : <></>}
            </div>
            <p className='info-text'>{t('requestClass.classInformation')}</p>
            <form
                onSubmit={(e): void => submitForm(state.responseData, {
                    'courseId': state.selectedCDICourseId,
                    'courseType': state.selectedCDICourseType,
                    'revisionDate': state.selectedRevisionDate,
                    'courseTitle': state.selectedCDICourseTitle
                })(e)}
            >
                <Grid
                    alignItems='center'
                    container
                    spacing={2}
                >
                    <Grid
                        className='cdi-grid'
                        item
                        xs={getCourseGridSize(requestType)}
                    >
                        <div>
                            <p className='required-field'>{t('requestClass.courseTitle')}</p>
                            <Select
                                disabled={requestType === 'Edit'}
                                hasSearch
                                name='courseTitle'
                                onChange={handleCourseChange}
                                placeholder={' '}
                                value={`${state.selectedCDICourseId} ${state.selectedCDICourseType} ${state.selectedCDICourseHours} ${state.selectedCDICourseMaxQty} ${state.selectedRevisionDate} ${state.selectedCDICourseTitle}`}
                            >
                                {cdiCourses.map((course: any) => (
                                    <Option
                                        key={`${course.courseId}:${course.courseType}`}
                                        value={`${course.courseId} ${course.courseType} ${course.courseHours} ${course.courseMaxQty} ${course.revisionDate} ${course.courseTitle}`}
                                    >
                                        {`${course.courseId} : ${course.courseTitle}`}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Grid>
                </Grid>
                <div className='division-block'/>
                <Grid
                    container
                    spacing={2}
                >
                    {Array.from({
                        length: state.totalSessions
                    }, (_, i) => (
                        <Grid
                            className={`cdi-grid session-${i + 1}-grid`}
                            item
                            key={`session-${i}`}
                            xs={getClassGridSize(requestType)}
                        >
                            <div><p className='info-text'>{t('requestClass.classSchedule')}</p></div>
                            <div><p className='info-text'>{t('requestClass.session')}</p></div>
                            <p className='required-field'>{t('requestClass.instructor')}</p>
                            {/* {state.selectedCDICourseTitle && state.setInstructors[0]?.instructorId === 'Loading...' ? <p className=''>{'Loading Instructors...'}</p> : null} */}
                            {checkLoader(state, t)}
                            <InstructorSelect
                                addRemoveInstructor={addRemoveInstructor}
                                buttonIndex={i}
                                courseId={state.selectedCDICourseId}
                                courseTitle={state.selectedCDICourseTitle}
                                courseType={state.selectedCDICourseType}
                                disabled={!state.selectedCDICourseTitle}
                                handleSecondaryInstructorChange={(value): void => handleSessionDataChange(value, `session-${i + 1}`, 'secondaryInstructor', {
                                    state,
                                    setState
                                })}
                                instructors={state.setInstructors}
                                maxSecondaryInstructors={state.maxSecondaryInstructors}
                                name={`session-${i + 1}-instructor`}
                                onChange={(e): void => handleSessionDataChange(e.target, `session-${i + 1}`, 'instructor', {
                                    state,
                                    setState
                                })}
                                placeholder=' '
                                secondaryInstructors={state.responseData[`session-${i + 1}`].secondaryInstructor}
                                sessionData={state.responseData[`session-${i + 1}`]}
                                setInstructorsData={(e): void => handleInstructorChange(e, state, setState, requestType)}
                                userType={userType}
                                value={state.responseData[`session-${i + 1}`].instructor}
                            />
                            {state.selectedCDICourseHours ? <p className='class-duration'>{t('requestClass.classDuration',
                                {
                                    hours: state.selectedCDICourseHours
                                })}</p> : null}
                            <div className='date-range-picker'>
                                <DateRangePicker
                                    EndInputProps={{
                                        id: `session-${i + 1}-endDate`,
                                        label: <span className='required-field'>{t('requestClass.endDate')}</span>,
                                        startAdornment: <InputAdornment position='end'><CalendarMonth/></InputAdornment>
                                    }}
                                    StartInputProps={{
                                        id: `session-${i + 1}-startDate`,
                                        helperText: t('requestClass.startDateHelper'),
                                        label: <span className='required-field'>{t('requestClass.startDate')}</span>,
                                        startAdornment: <InputAdornment position='start'><CalendarMonth/></InputAdornment>
                                    }}
                                    dateFormat='m/d/y'
                                    endDatePlaceholder={t('requestClass.selectEndDate')}
                                    id={`session-${i + 1}-dates-startDate`}
                                    minDate={new Date(new Date().setDate(new Date().getDate() + classStartDay)).toLocaleDateString('en-CA')}
                                    onChange={(dates: Date[]): void => {
                                    // Only trigger the handler when both dates are selected
                                        if (dates.length === 2) {
                                            handleSessionDataChange({
                                                value: {
                                                    startDate: dates[0],
                                                    endDate: dates[1]
                                                }
                                            }, `session-${i + 1}`, 'dates', {
                                                state,
                                                setState
                                            });
                                        }
                                    }}
                                    placeholder={' '}
                                    size='medium'
                                    startDatePlaceholder={t('requestClass.selectStartDate')}
                                    value={[
                                        dateConverter(state.responseData[`session-${i + 1}`].dates.startDate),
                                        dateConverter(state.responseData[`session-${i + 1}`].dates.endDate)
                                    ]}
                                />
                            </div>
                            <div className='additional-margin'>
                                <DatePicker
                                    InputProps={{
                                        helperText: t('requestClass.startTimeHelper'),
                                        label: <span className='required-field'>{t('requestClass.startTime')}</span>,
                                        startAdornment: <InputAdornment position='start'><Schedule/></InputAdornment>
                                    }}
                                    className={'highlight'}
                                    enableTime
                                    flatpickrOptions={{
                                        enableTime: true,
                                        minuteIncrement: 1,
                                        defaultHour: DEFAULT_START_HOUR, // Set default hour to 8 AM
                                        defaultMinute: 0 // Set default minute to 0
                                    }}
                                    id={`session-${i + 1}-startTime`}
                                    noCalendar
                                    onChange={(dates: Date[]): void => handleSessionDataChange({
                                        value: dates[0]

                                    }, `session-${i + 1}`, 'startTime', {
                                        state,
                                        setState
                                    })}
                                    placeholder={' '}
                                    required
                                    value={dateConverter(state.responseData[`session-${i + 1}`].startTime)}
                                />
                            </div>
                            <div className='additional-margin'>
                                <DatePicker
                                    InputProps={{
                                        helperText: t('requestClass.endTimeHelper'),
                                        label: <span className='required-field'>{t('requestClass.endTime')}</span>,
                                        startAdornment: <InputAdornment position='start'><Schedule/></InputAdornment>
                                    }}
                                    enableTime
                                    flatpickrOptions={{
                                        enableTime: true,
                                        minuteIncrement: 1,
                                        disable: [
                                            (date: Date): boolean => {
                                                // Disable times before the default end time
                                                const defaultEndHours = state.responseData[`session-${i + 1}`].defaultEndHours;
                                                const defaultEndMinutes = state.responseData[`session-${i + 1}`].defaultEndMinutes;

                                                return date.getHours() < defaultEndHours || date.getHours() === defaultEndHours && date.getMinutes() < defaultEndMinutes;
                                            }
                                        ],
                                        defaultHour: state.responseData[`session-${i + 1}`].defaultEndHours,
                                        defaultMinute: state.responseData[`session-${i + 1}`].defaultEndMinutes
                                    }}
                                    id={`session-${i + 1}-endTime`}
                                    noCalendar
                                    onChange={(dates: Date[]): void => handleSessionDataChange({
                                        value: dates[0]
                                    }, `session-${i + 1}`, 'endTime', {
                                        state,
                                        setState
                                    })}
                                    placeholder={' '}
                                    value={dateConverter(state.responseData[`session-${i + 1}`].endTime)}
                                />
                            </div>
                            <div
                                className='time-zone-container'
                                style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <p
                                    className='required-field'
                                    style={{
                                        marginRight: '0.3em'
                                    }}
                                >{t('requestClass.timeZone')}
                                </p>
                                <span className='move-emoji'>{'👉'}</span>
                                <a
                                    href='https://johndeere.widen.net/s/r6zzchqtkg/job-aid---jdu-admin-app---time-zones-to-select'
                                    rel='noopener noreferrer'
                                    style={{
                                        marginLeft: '0.3em',
                                        fontWeight: 'normal',
                                        fontSize: '0.7em'
                                    }}
                                    target='_blank'
                                > {t('requestClass.timeZoneGuide')}</a>
                            </div>
                            <Select
                                hasSearch
                                name={`session-${i + 1}-timeZone-0-timezoneId`}
                                onChange={(e): void => handleSessionDataChange(e.target, `session-${i + 1}`, 'timeZone', {
                                    state,
                                    setState
                                })}
                                placeholder={' '}
                                value={state.responseData[`session-${i + 1}`].timeZone}
                            >
                                {state.timeZones.map((timeZone) => (
                                    <Option
                                        key={timeZone.timezoneId}
                                        value={timeZone.timezoneId}
                                    >
                                        {`${timeZone.description} (${timeZone.timezoneId})`}
                                    </Option>))}
                            </Select>
                            <p><b>{t('requestClass.trainingLocation')}</b></p>
                            {showVirtualClassLinkField(state, setState, i, t)}
                            <TextField
                                id={`session-${i + 1}-dealerFacility`}
                                inputProps={{
                                    maxLength: 90,
                                    readOnly: itemType.includes(state.selectedCDICourseType)
                                }}
                                label={<span className='required-field'>{t('requestClass.facilityAddress')}</span>}
                                onChange={(e): void => handleSessionDataChange(e.target, `session-${i + 1}`, 'dealerFacility', {
                                    state,
                                    setState
                                })}
                                value={state.responseData[`session-${i + 1}`].dealerFacility}
                            />
                            <p><b>{t('requestClass.classSize')}</b></p>
                            <p className='required-field'>{t('requestClass.maxQty')}</p>
                            <Select
                                name={`session-${i + 1}-maxQty`}
                                onChange={(e): void => handleSessionDataChange({
                                    value: e.target.value
                                }, `session-${i + 1}`, 'maxQty', {
                                    state,
                                    setState
                                })}
                                placeholder={' '}
                                value={getMaxQtyValue(`session-${i + 1}`, state)}
                            >
                                {state.maxQty.map((qty: string) => (
                                    <Option
                                        key={qty}
                                        value={qty}
                                    >
                                        {qty}
                                    </Option>
                                ))}
                            </Select>
                            <div className='additional-margin'>
                                <TextField
                                    id='comments'
                                    inputProps={{
                                        maxLength: 1500
                                    }}
                                    label={<span className='optional-field'>{t('requestClass.additionalComments')}</span>}
                                    minRows={5}
                                    multiline={true}
                                    onChange={(e): void => handleSessionDataChange(e.target, `session-${i + 1}`, 'additionalComments', {
                                        state,
                                        setState
                                    })}
                                    value={state.responseData[`session-${i + 1}`].additionalComments}
                                />
                            </div>
                            <Button
                                className='copy-session-button'
                                disabled={requestType === 'Edit'}
                                onClick={(): void => handleSessionAdd(`session-${i + 1}`)}
                                startIcon={<Add/>}
                                variant={'text-inline'}
                            >
                                {t('requestClass.copySession')}
                            </Button>
                            {i > 0 &&
                                <Button
                                    className='delete-session-button'
                                    onClick={(): void => handleSessionDelete(`session-${i + 1}`)}
                                    startIcon={<Delete/>}
                                    variant={'text-inline'}
                                >
                                    {t('requestClass.deleteSession')}
                                </Button>}
                        </Grid>
                    ))}
                </Grid>
                <b>{t('requestClass.cancellationPolicy')}</b>
                <br/>
                <br/>
                <Button
                    type='submit'
                    variant='primary'
                >
                    {t('requestClass.submit')}
                </Button>
            </form>
        </Container>
    );
};

export default RequestClass;
